'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

const muglaProduct: Product = {
  slug: 'mugla-bondex-sunger',
  name: 'Muğla Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'Muğla bondex sünger; villa, otel, apart ve konut projelerinde ses ve titreşim kontrolü için yüksek yoğunluklu çözüm sunar.',
  description:
    'Muğla bondex sünger; rebonded süngerpan yapısıyla darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olan, Muğla içi projelere özel ses yalıtım çözümüdür.',
  imagePath: '/images/bondex-9.webp',
  sku: 'BDX-MGL',
  mpn: 'BDX-MGL',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'Muğla bondex sünger hangi yapılarda kullanılır?',
      answer:
        'Villa, apart, butik otel, yazlık site, ofis ve teknik hacimlerde kullanılır. Muğla’da özellikle sezonluk kullanım yoğunluğu olan yapılarda tercih edilir.',
    },
    {
      question: 'Muğla içinde teslimat süresi ne kadar?',
      answer:
        'Stok durumuna bağlı olarak merkez ve ilçelerde teslimatlar çoğunlukla 1-3 iş günü içinde planlanır. Büyük projelerde etaplı sevkiyat yapılabilir.',
    },
    {
      question: 'Doğru kalınlık nasıl belirlenir?',
      answer:
        'Hafif şikayetlerde 10-20mm, orta ve yüksek ihtiyaçlarda 30-40mm, zorlayıcı teknik durumlarda 50mm ve bariyerli kombinasyonlar önerilir.',
    },
    {
      question: "Muğla'da teknik keşif desteği var mı?",
      answer:
        'Evet. Proje metrajı, kullanım tipi ve hedef konfora göre katman önerisi ve uygulama planlaması yapılır.',
    },
    {
      question: 'Muğla bondex sünger fiyatı ne kadar?',
      answer:
        "Muğla bondex sünger fiyatı metrekare (m²) başına 130 TL'den başlar. Net fiyat kalınlık, metraj ve uygulama senaryosuna göre değişir.",
    },
  ],
  reviews: [
    {
      authorName: 'Gökhan Yılmaz',
      datePublished: '2026-04-05',
      ratingValue: 5,
      title: 'Bodrum villada ciddi fark',
      body:
        "Bodrum'daki villa projemizde üst kat darbe sesi için uyguladık. Gece konforu belirgin şekilde arttı.",
      verified: true,
    },
    {
      authorName: 'Elif Kaya',
      datePublished: '2026-04-11',
      ratingValue: 5,
      title: 'Marmaris apart otelde memnunuz',
      body:
        'Koridor ve teknik hacim kaynaklı gürültüyü düşürmek için kullandık. Müşteri şikayetleri azaldı.',
      verified: true,
    },
    {
      authorName: 'Can Demir',
      datePublished: '2026-04-16',
      ratingValue: 4,
      title: 'Fethiye ofisinde iyi sonuç',
      body:
        'Toplantı odasında ses taşması için uygulama yaptık. Beklediğimiz seviyede bir iyileşme aldık.',
      verified: true,
    },
  ],
};

export function CityBondexMugla() {
  const url = toAbsoluteUrl('/mugla-bondex-sunger');
  const title = 'Muğla Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'Muğla bondex sünger, villa ve turizm projelerinde darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olur. Echopan kalitesiyle sunulur.';

  const relatedSlugs = ['bondex-sunger', 'bondex-sunger-30mm', 'bondex-sunger-50mm', 'bariyerli-bondex-sunger'];
  const relatedBondex = relatedSlugs.map((slug) => products.find((p) => p.slug === slug)).filter((p): p is Product => Boolean(p));

  const waUrl = buildWhatsAppUrl('Merhaba, Muğla bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (mugla-bondex-sunger)');
  const displayPrice = muglaProduct.priceTRY
    ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(muglaProduct.priceTRY)
    : null;

  const pageReviews =
    muglaProduct.reviews?.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    })) || [];
  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((sum, r) => sum + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  const topSpecs = [
    { k: 'Stok', v: 'Muğla içi düzenli stok' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Kalınlık', v: '10mm-50mm' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
    { k: 'Hizmet', v: 'Muğla merkez + ilçeler' },
    { k: 'Plan', v: 'Sezon ve proje bazlı' },
  ];

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'Muğla Bondex Sünger', item: url },
  ];

  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${toAbsoluteUrl('/')}#website`,
        name: 'Bondex Sünger',
        url: toAbsoluteUrl('/'),
        inLanguage: 'tr-TR',
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${toAbsoluteUrl('/')}#organization`,
        name: 'bariyerlibondexsunger.com',
        url: toAbsoluteUrl('/'),
        logo: { '@type': 'ImageObject', url: toAbsoluteUrl('/images/bondex-logo.webp'), width: 512, height: 512 },
        contactPoint: [
          { '@type': 'ContactPoint', telephone: '+90 216 519 34 11', contactType: 'customer service', areaServed: 'TR', availableLanguage: ['tr'] },
          { '@type': 'ContactPoint', telephone: '+90 546 919 11 34', contactType: 'sales', areaServed: 'TR', availableLanguage: ['tr'] },
        ],
        sameAs: [
          'https://www.facebook.com/profile.php?id=61575206163572',
          'https://x.com/KabinPods',
          'https://www.instagram.com/kabinpods/',
          'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
          'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
        ],
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        inLanguage: 'tr-TR',
        datePublished: '2026-04-27',
        dateModified: new Date().toISOString().split('T')[0],
        primaryImageOfPage: { '@type': 'ImageObject', url: toAbsoluteUrl(muglaProduct.imagePath), width: 1200, height: 750 },
        about: [
          { '@type': 'Thing', name: 'Muğla Bondex Sünger' },
          { '@type': 'Thing', name: 'villa ses yalıtımı' },
          { '@type': 'Thing', name: 'turizm yapısı akustik çözümü' },
        ],
        speakable: { '@type': 'SpeakableSpecification', cssSelector: ['.ProductSEO', '.ProductPage h1'] },
        mainEntity: { '@id': `${url}#product` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumb.map((b, idx) => ({ '@type': 'ListItem', position: idx + 1, name: b.name, item: b.item })),
      },
      {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: muglaProduct.name,
        image: [{ '@type': 'ImageObject', url: toAbsoluteUrl(muglaProduct.imagePath), width: 1200, height: 750 }],
        description: muglaProduct.description,
        brand: { '@type': 'Brand', name: 'Bondex' },
        category: muglaProduct.category,
        url,
        sku: muglaProduct.sku,
        mpn: muglaProduct.mpn,
        productID: muglaProduct.sku || muglaProduct.mpn,
        material: 'Rebonded Süngerpan',
        audienceType: 'Business',
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Kategori', value: muglaProduct.category },
          { '@type': 'PropertyValue', name: 'Tip', value: 'Rebonded Süngerpan' },
          { '@type': 'PropertyValue', name: 'Hizmet Bölgesi', value: 'Muğla' },
        ],
        offers: {
          '@type': 'Offer',
          url,
          priceCurrency: 'TRY',
          price: String(muglaProduct.priceTRY),
          validFrom: validFromStr,
          priceValidUntil: priceValidUntilStr,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
          unitText: 'm²',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: String(muglaProduct.priceTRY), priceCurrency: 'TRY', unitCode: 'MTK', unitText: 'm²' },
          hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
          shippingDetails: { '@id': `${url}#shippingDetails` },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: avgRating.toFixed(1),
          reviewCount: pageReviews.length,
          bestRating: '5',
          worstRating: '1',
        },
        review: muglaProduct.reviews?.map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.authorName },
          datePublished: r.datePublished,
          reviewBody: r.body,
          headline: r.title,
          ...(r.verified && { reviewAspect: 'Verification' }),
          reviewRating: { '@type': 'Rating', ratingValue: String(r.ratingValue), bestRating: '5', worstRating: '1' },
        })),
      },
      {
        '@type': 'MerchantReturnPolicy',
        '@id': `${url}#merchantReturnPolicy`,
        applicableCountry: { '@type': 'Country', name: 'TR' },
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      {
        '@type': 'OfferShippingDetails',
        '@id': `${url}#shippingDetails`,
        shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'TRY' },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: { '@type': 'Country', name: 'TR' } },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
          businessDays: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
          cutoffTime: '14:00',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: muglaProduct.faqs?.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
      },
    ],
  };

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Açıklama',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Muğla'da Bondex Sünger Kullanımında Öne Çıkan Gerçekler</h2>
            <p className={styles.seoText}>
              Muğla'da ses yalıtımı ihtiyacı, şehir merkezli standart yaklaşımlardan farklı bir dinamikte gelişir. Çünkü kentte aynı anda
              yoğun turizm yapıları, sezonluk konutlar, villa yerleşimleri ve teknik hacim ağırlıklı ticari projeler bulunur. Bodrum, Marmaris,
              Fethiye, Datça, Milas, Ortaca ve merkez Muğla tarafında yapı karakteri değiştikçe ses şikayetinin türü de değişir. Bir bölgede
              komşu kaynaklı darbe sesi öne çıkarken başka bir bölgede mekanik tesisat veya teknik oda kaynaklı uğultu daha baskın hale gelir.
            </p>
            <p className={styles.seoText}>
              Bu nedenle Muğla bondex sünger çözümünde temel kural, ürün adını ezbere seçmek değil; kullanım amacını doğru tanımlamaktır.
              Rebonded süngerpan yapısı, darbe ve titreşim enerjisinin yüzeyler üzerinden taşınmasını azaltmaya yardımcı olur. Fakat gerçek
              performans, doğru uygulama detaylarıyla ortaya çıkar: yüzey hazırlığı, birleşim sürekliliği, kenar dönüşleri, katman sıralaması
              ve proje termininin doğru yönetimi sonucu belirler.
            </p>
            <h3 className={styles.seoSubtitle}>Muğla'da İlçe Bazlı Farklılaşan Senaryolar</h3>
            <p className={styles.seoText}>
              Bodrum tarafında villa ve sezonluk konaklama yapıları ağırlıktadır. Bu yapılarda kullanıcı beklentisi daha çok gece sessizliği,
              mekanik ekipman etkisinin azaltılması ve odalar arası konforun korunmasıdır. Marmaris ve Fethiye hattında turizm tesisleri
              nedeniyle koridor, teknik hacim ve ortak alan kaynaklı sesler öne çıkar. Milas ve Ortaca tarafında ise konut + teknik hacim
              karışımı daha sık görülür. Bu farklı tablo, tek kalınlık ve tek uygulama modelinin tüm şehirde çalışmayacağını net biçimde gösterir.
            </p>
            <p className={styles.seoText}>
              Muğla'da çözüm planlarken "hangi ilçe?" sorusu sadece lojistik için değil teknik tercih için de önemlidir. Kıyı bölgelerinde
              sezon yoğunluğu nedeniyle uygulama zamanlaması kritik olurken, iç bölgelerde yenileme işlerinde yapı yaşı ve yüzey durumu daha
              belirleyici olabilir. Bu yüzden proje başında doğru teşhis, toplam maliyetin ve sonucu etkileyen en kritik adımdır.
            </p>
            <h3 className={styles.seoSubtitle}>Konut ve Villa Projelerinde Öncelik Nedir?</h3>
            <p className={styles.seoText}>
              Konut ve villa projelerinde kullanıcıların en sık talebi, komşu veya kat kaynaklı seslerin azaltılmasıdır. Özellikle yazlık
              kullanımın yoğun olduğu bölgelerde sezon döneminde kısa sürede çözüm talebi artar. Ancak hızlı uygulama ile doğru uygulama her
              zaman aynı şey değildir. Katman detayları atlandığında sorun geçici olarak azalır, sonra tekrar eder. Kalıcı sonuç için kritik
              geçiş noktaları doğru ele alınmalıdır.
            </p>
            <p className={styles.seoText}>
              Villa projelerinde havuz ekipman odası, mekanik hacim, servis alanı ve yaşam alanı arasındaki ses transferi ayrıca önem taşır.
              Bu alanlarda kaynak noktaya yakın kurgulanan çözüm, yaşam hacimlerinde hissedilen gürültüyü ciddi oranda azaltabilir. Muğla'da
              villa projelerinin artması nedeniyle bu teknik detay, sahada her geçen yıl daha kritik hale gelmektedir.
            </p>
            <h3 className={styles.seoSubtitle}>Turizm Yapılarında Kullanım Deneyimi Neden Önemli?</h3>
            <p className={styles.seoText}>
              Muğla'da otel, apart otel ve kısa dönem kiralama yapılarında ses yönetimi doğrudan müşteri memnuniyetiyle ilişkilidir. Oda içi
              konfor, koridor gürültüsü, teknik oda etkisi ve komşu oda ses geçişi işletme puanlarını etkileyebilir. Bu sebeple çözüm sadece
              "malzeme alıp uygulama" şeklinde değil, işletme takvimiyle uyumlu planlanmalıdır. Sezon öncesi etaplama, yoğun dönem için hız
              planı ve bakım erişimi korunmuş uygulama modeli birlikte yürütülmelidir.
            </p>
            <p className={styles.seoText}>
              Özellikle aktif tesiste yapılan işlerde çalışma saatleri, servis koridoru kullanımı ve operasyon aksını bozmama kriterleri önemlidir.
              Bu planlama yapılmadan hızlı teslim hedeflenirse proje yönetimi zorlaşır. Muğla'nın turizm gerçekliğinde doğru yöntem; kontrollü
              etap, doğru teknik kontrol ve sürdürülebilir uygulama disiplinidir.
            </p>
            <h3 className={styles.seoSubtitle}>Kalınlık Seçimi ve Model Kıyaslaması</h3>
            <p className={styles.seoText}>
              Kalınlık seçerken ilk kriter "en kalın ürün" değil, "en doğru senaryo eşleşmesi" olmalıdır. Hafif-orta şikayetlerde 20-30mm
              çoğu projede dengeli sonuç verir. Daha zorlu teknik hacimlerde 40mm ve 50mm çözümler gündeme gelir. Bazı projelerde ise bariyerli
              sistem kombinasyonları daha verimli olur. Bu noktada genel ürün ailesi için{' '}
              <Link to="/bondex-sunger" rel="follow">bondex sünger</Link> ve ileri seviye ihtiyaca göre{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">bariyerli bondex</Link> karşılaştırması yapılmalıdır.
            </p>
            <p className={styles.seoText}>
              Gereksiz kalınlık kullanımı maliyeti artırır ama her zaman daha iyi sonuç vermez. Kritik noktalara hedefli güçlendirme ve düşük
              riskli bölgelerde optimize katman yaklaşımı, çoğu Muğla projesinde daha iyi maliyet-performans dengesi sağlar.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Muğla Projelerinde Teknik Seçim Çerçevesi</h2>
            <h3 className={styles.seoSubtitle}>Malzeme Davranışı</h3>
            <p className={styles.seoText}>
              Bondex sünger, yüksek yoğunluklu yapısıyla darbe ve titreşim kaynaklı enerjiyi düşürmeye yardımcı olur. Uygulama sonucu,
              katman sürekliliği ve birleşim kalitesiyle doğrudan bağlantılıdır.
            </p>
            <h3 className={styles.seoSubtitle}>Saha Disiplini</h3>
            <p className={styles.seoText}>
              Yüzey temizliği, doğru yapıştırıcı seçimi ve ek yerlerinin kontrolü yapılmadan tamamlanan uygulamalarda performans kaybı oluşabilir.
              Bu nedenle teknik kontrol adımlarının atlanmaması gerekir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Muğla'da Sık Kullanılan Uygulama Alanları</h2>
            <h3 className={styles.seoSubtitle}>Konut ve Villa</h3>
            <p className={styles.seoText}>
              Katlar arası darbe sesi, oda geçişleri ve teknik hacim etkisini azaltmak için zemin/duvar katmanlarında kullanılır.
            </p>
            <h3 className={styles.seoSubtitle}>Turizm Yapıları</h3>
            <p className={styles.seoText}>
              Oda konforu, koridor sessizliği ve mekanik hacim kaynaklı rahatsızlıkların düşürülmesi için tercih edilir.
            </p>
            <h3 className={styles.seoSubtitle}>Ofis ve Teknik Oda</h3>
            <p className={styles.seoText}>
              Toplantı odası mahremiyeti ve ekipman kaynaklı titreşim/gürültü kontrolünde katmanlı çözümlerde kullanılır.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Opsiyonel',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teslimat ve Proje Akışı</h2>
            <h3 className={styles.seoSubtitle}>Sevkiyat Planı</h3>
            <p className={styles.seoText}>
              Muğla merkez ve ilçelerde sevkiyat, saha erişimine göre planlanır. Büyük metrajlı işlerde etaplı teslimat uygulama verimini artırır.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Yönlendirme</h3>
            <p className={styles.seoText}>
              Keşif, metraj doğrulama, katman önerisi ve uygulama sonrası kontrol ile proje süreci daha tutarlı hale getirilir.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      <JsonLd data={schema} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Muğla Bondex Sünger</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img className={styles.img} src={muglaProduct.imagePath} alt="Muğla bondex sünger uygulaması" loading="eager" width="600" height="400" />
        </div>
        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>Muğla Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-MGL | Muğla İçi Projeler
          </p>
          <p className={styles.meta}>
            Muğla'da <strong>bondex sünger</strong> ihtiyacı olan villa, konut, otel ve teknik hacim projeleri için doğru kalınlık seçimi,
            hızlı tedarik ve proje bazlı uygulama planlaması sunuyoruz.
          </p>

          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">Muğla içi hızlı teslimat</span>
            <span className="chip">Villa ve turizm yapısına uygun</span>
          </div>

          <div className={styles.specGrid} aria-label="Muğla için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h3 className={styles.reviewName}>{review.title}</h3>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {muglaProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Muğla Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={muglaProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
